// Block list for Community. Apple Guideline 1.2 requires users be able to
// block abusive users in any app with user-generated content: a blocked
// user's posts and replies are filtered out of everything the blocker sees.
//
// The list is read from localStorage, so a block takes effect instantly with
// no round trip, and saved to the account (user_data "blocked_users") so it
// follows the coach to every device. It used to be per device only, so a
// coach who blocked someone on their phone still saw them on the web.

import { loadUserData, saveUserData } from "../utils/userData";

const KEY = "bam_blocked_users";
// Set once this device has merged its local list into the account's. After
// that the account's list is the truth, so an unblock on one device sticks.
const SYNCED = "bam_blocked_users_synced";
const DATA_KEY = "blocked_users";

const clean = (arr) => (Array.isArray(arr) ? arr.filter(Boolean) : []);

export function getBlockedIds() {
  try {
    const raw = localStorage.getItem(KEY);
    return clean(raw ? JSON.parse(raw) : []);
  } catch {
    return [];
  }
}

export function isBlocked(userId) {
  if (!userId) return false;
  return getBlockedIds().includes(userId);
}

function setLocal(next) {
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch {}
  try { window.dispatchEvent(new Event("bam-blocklist-changed")); } catch {}
}

function saveRemote(next) {
  Promise.resolve().then(() => saveUserData(DATA_KEY, next)).catch(() => { /* local copy still applies; next sync retries */ });
}

export function blockUser(userId) {
  if (!userId) return getBlockedIds();
  const next = Array.from(new Set([...getBlockedIds(), userId]));
  setLocal(next);
  saveRemote(next);
  return next;
}

export function unblockUser(userId) {
  const next = getBlockedIds().filter((id) => id !== userId);
  setLocal(next);
  saveRemote(next);
  return next;
}

/** What this device should hold after syncing with the account. First sync
 *  on a device merges both lists (so no block is lost); after that the
 *  account's list wins. Pure, for the tests. */
export function mergeBlocklists(local, remote, alreadySynced) {
  return alreadySynced ? clean(remote) : Array.from(new Set([...clean(local), ...clean(remote)]));
}

/** Pull the account's list down (and push a first-time merge up). Called
 *  once when the signed-in app loads. */
export async function syncBlocklist() {
  let synced = false;
  try { synced = localStorage.getItem(SYNCED) === "1"; } catch {}
  const remote = await loadUserData(DATA_KEY, [], { forceRefresh: true }).catch(() => null);
  if (!Array.isArray(remote)) return getBlockedIds();
  const local = getBlockedIds();
  const next = mergeBlocklists(local, remote, synced);
  const changedRemote = !synced && next.length !== clean(remote).length;
  if (changedRemote) saveRemote(next);
  try { localStorage.setItem(SYNCED, "1"); } catch {}
  if (next.length !== local.length || next.some(id => !local.includes(id))) setLocal(next);
  return next;
}
